import { create } from 'zustand';
import {
  integrationActions,
  IntegrationConnectionListItem,
} from './integration.actions';
import { SupportedIntegrationPlatform } from '../generated/gql-types';

export type IntegrationStoreStatus = 'idle' | 'loading' | 'ready' | 'error';

const inFlightRequests = new Map<string, Promise<void>>();

interface IntegrationState {
  integrationConnections: IntegrationConnectionListItem[];
  integrationPlatformByIntegrationId: Record<string, SupportedIntegrationPlatform>;
  integrationStatus: IntegrationStoreStatus;
  loadedTenantId: string | null;
  setIntegrationData: (
    tenantId: string,
    connections: IntegrationConnectionListItem[],
    platformByIntegrationId: Record<string, SupportedIntegrationPlatform>,
  ) => void;
  setIntegrationStatus: (integrationStatus: IntegrationStoreStatus) => void;
  clearIntegrationCache: () => void;
  fetchAndSetIntegrationsForTenant: (tenantId: string) => Promise<void>;
  ensureIntegrationConnections: (tenantId: string) => Promise<void>;
  getIntegrationsForTenant: () => IntegrationConnectionListItem[];
  getIntegrationByPlatform: (
    platform: SupportedIntegrationPlatform,
  ) => IntegrationConnectionListItem | null;
}

export const useIntegrationStore = create<IntegrationState>((set, get) => ({
  integrationConnections: [],
  integrationPlatformByIntegrationId: {},
  integrationStatus: 'idle',
  loadedTenantId: null,
  setIntegrationData: (tenantId, connections, platformByIntegrationId) =>
    set({
      integrationConnections: connections,
      integrationPlatformByIntegrationId: platformByIntegrationId,
      integrationStatus: 'ready',
      loadedTenantId: tenantId,
    }),
  setIntegrationStatus: (integrationStatus) => set({ integrationStatus }),
  clearIntegrationCache: () =>
    set({
      integrationConnections: [],
      integrationPlatformByIntegrationId: {},
      integrationStatus: 'idle',
      loadedTenantId: null,
    }),
  fetchAndSetIntegrationsForTenant: async (tenantId: string) => {
    set({ integrationStatus: 'loading' });
    const result =
      await integrationActions.fetchIntegrationDataForTenant(tenantId);
    if (result) {
      set({
        integrationConnections: result.connections,
        integrationPlatformByIntegrationId: result.platformByIntegrationId,
        integrationStatus: 'ready',
        loadedTenantId: tenantId,
      });
    } else {
      set({ integrationStatus: 'error' });
    }
  },
  ensureIntegrationConnections: async (tenantId: string) => {
    const {
      loadedTenantId,
      integrationStatus,
      fetchAndSetIntegrationsForTenant,
    } = get();

    if (loadedTenantId === tenantId && integrationStatus === 'ready') {
      return;
    }

    if (inFlightRequests.has(tenantId)) {
      await inFlightRequests.get(tenantId)!;
      return;
    }

    const request = fetchAndSetIntegrationsForTenant(tenantId).finally(() => {
      inFlightRequests.delete(tenantId);
    });
    inFlightRequests.set(tenantId, request);
    await request;
  },
  getIntegrationsForTenant: () => get().integrationConnections,
  getIntegrationByPlatform: (platform: SupportedIntegrationPlatform) => {
    const { integrationConnections, integrationPlatformByIntegrationId } = get();
    return (
      integrationConnections.find(
        (integrationConnection) =>
          integrationPlatformByIntegrationId[
            integrationConnection.integrationId
          ] === platform,
      ) ?? null
    );
  },
}));
